import { zodResolver } from '@hookform/resolvers/zod'
import { Checkbox } from '@progress/kendo-react-inputs'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { VStack } from '~/components/elements/VStack'
import { type RoleName } from '~/gql/graphql'
import { useFormField } from '~/hooks/useFormField'
import { useIsCoreMember } from '~/hooks/useIsCoreMember'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { useNotifyOnError } from '~/hooks/useNotifyOnError'
import { ensureExists } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import {
  userRole,
  userRoleNameToLabel,
  userRoleNameToRoleId,
} from '../constants'
import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useChangeUserRoleOnBrandAccountMutation } from '../hooks/useChangeUserRoleOnBrandAccountMutation'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useCurrentBrandAccountUserRole } from '../hooks/useCurrentBrandAccountUserRole'
import {
  getRoleNameWithAccessToSetting,
  getRoleNameWithoutAccessToSetting,
  hasAccessToSetting,
  isMForce,
  isPartner,
  isPartnerManager,
  isUser,
  isUserManager,
} from '../utils'

import { UserRoleTypeSelector } from './UserRoleTypeSelector'

const getSelectableUserRoleNames = (roleName: RoleName): RoleName[] => {
  if (isPartnerManager(roleName) || isPartner(roleName)) {
    return ['PARTNER_MANAGER', 'PARTNER']
  }
  if (isUserManager(roleName) || isUser(roleName)) {
    return ['USER_MANAGER', 'USER']
  }

  return []
}

const schema = z.object({
  role: z.string().refine((value) => R.isIncludedIn(value, userRole)),
  canAccessSetting: z.boolean(),
})
type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export const EditBrandUserDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  if (dialogInfo?.dialogType !== 'edit') {
    throw new Error()
  }

  const { brandAccountUser } = dialogInfo
  const brandAccountUserRole = ensureExists(
    brandAccountUser.role,
    t('assertion_failed'),
  )

  const brandAccountId = useBrandAccountId()
  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const notify = useNotify()
  const notifyError = useNotifyOnError()
  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()

  const isCoreMember = useIsCoreMember()
  const currentBrandAccountUserRole =
    useCurrentBrandAccountUserRole(brandAccountId)

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: {
      role: getRoleNameWithoutAccessToSetting(brandAccountUserRole.name),
      canAccessSetting: hasAccessToSetting(brandAccountUserRole.name),
    },
    resolver,
  })

  const canAccessSettingField = useFormField(control, 'canAccessSetting')

  const attributes = React.useMemo(() => {
    const selectableUserRoleTypes = getSelectableUserRoleNames(
      brandAccountUserRole.name,
    )

    return R.map(selectableUserRoleTypes, (userRoleType) => ({
      text: t(userRoleNameToLabel[userRoleType]),
      value: userRoleType,
    }))
  }, [brandAccountUserRole.name, t])

  const translatedRole = brandAccountUser.role
    ? t(userRoleNameToLabel[brandAccountUser.role.name])
    : ''

  const changeUserRoleOnBrandAccountMutation =
    useChangeUserRoleOnBrandAccountMutation({
      onSuccess: () => {
        notify({
          content: t('user_role_changed', {
            role: translatedRole,
          }),
          style: 'success',
        })

        // 役割変更と設定アクセス権限の付与は同じダイアログの1回の送信で行われるため、
        // EditBrandAccountDialogと同様に分けず1イベントにまとめる。
        logEvent('Edit brand account user', {
          eventProperties: [{ key: 'email', value: brandAccountUser.email }],
        })

        closeDialogWithRefresh()
      },
      onError: (data) => {
        notifyError(data, {
          400: t('cannot_change_the_last_administrator_to_nonadministrator', {
            role: translatedRole,
          }),
          403: t('no_permission_to_change_roles'),
          422: t('cannot_change_roles_across_partitions'),
        })
        resetDialogInfo()
      },
    })

  // NOTE: 現在操作中のユーザーの権限がMFORCE、または、コアメンバーの場合のみ設定へのアクセス権限を変更可能です
  const hasEditAccessSettingRole =
    isCoreMember || isMForce(currentBrandAccountUserRole.name)
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ canAccessSetting, role }) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      // NOTE: https://linear.app/glucose/issue/9SEGS-320/パートナー管理者がパートナーを昇格できない問題について調査を行う#comment-ac324206
      const nextRoleName =
        canAccessSetting && hasEditAccessSettingRole
          ? getRoleNameWithAccessToSetting(role)
          : getRoleNameWithoutAccessToSetting(role)

      changeUserRoleOnBrandAccountMutation.mutate({
        brandAccountId,
        userId: brandAccountUser.id,
        roleId: userRoleNameToRoleId[nextRoleName],
      })
    },
    [
      brandAccountId,
      brandAccountUser.id,
      changeUserRoleOnBrandAccountMutation,
      isDirty,
      hasEditAccessSettingRole,
      resetDialogInfo,
    ],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={false}
      mode="settings"
      title={t('brandaccountuser_role_displayname_setting_change', {
        brandAccountUser: {
          role: { displayName: translatedRole },
        },
      })}
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <VStack spacing={4}>
        {attributes.length !== 0 && (
          <LabelingContainer label={t('role')}>
            <Controller
              control={control}
              name="role"
              render={({ field }) => (
                <UserRoleTypeSelector
                  attributes={attributes}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </LabelingContainer>
        )}

        {hasEditAccessSettingRole && (
          <Checkbox
            label={t('access_to_settings')}
            {...canAccessSettingField}
          />
        )}
      </VStack>
    </ExecuteCancelDialog>
  )
}
